import CloseIcon from "@mui/icons-material/Close";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { colors } from "../../../utils";
import { ASSETS } from "../../../utils/config";
import { transformedText } from "../../../utils/helpers";
import Button from "../../Button";
import TextView from "../../TextView";
import UserImage from "../../UserImage";
import GallerySettings from "../GallerySettings";
import { Row, SocialLinkItem, SocialLinksWrapper, TextWrapper } from "./style";

import { SocialIcons, SocialLinks } from "../../../utils/socialHelpers";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  ".MuiBackdrop-root": {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  ".MuiPaper-root": {
    border: "1px solid #E1E1E1",
    borderRadius: 8,
    width: "40vw",
    maxHeight: "90vh",
  },
}));

export let ProfilePopProps = {};

const ProfilePopup = ({ open: openProp = false, onCancel }) => {
  const [isGalleryOpen, setGalleryOpen] = useState(false);
  const [open, setOpen] = useState(openProp);
  const [socialLinks, setSocialLinks] = useState([]);
  const [ctaButtons, setCtaButtons] = useState([]);
  const { userProfile } = useSelector((state) => state.userProfile);
  const [profile, setProfile] = useState(userProfile);

  const social_button_asset_mapping = {
    [SocialLinks.mail]: SocialIcons[SocialLinks.mail],
    [SocialLinks.instagram]: SocialIcons[SocialLinks.instagram],
    [SocialLinks.facebook]: SocialIcons[SocialLinks.facebook],
    [SocialLinks.twitter]: SocialIcons[SocialLinks.twitter],
    [SocialLinks.pinterest]: SocialIcons[SocialLinks.pinterest],
    [SocialLinks.youtube]: SocialIcons[SocialLinks.youtube],
    [SocialLinks.web]: SocialIcons[SocialLinks.web],
    [SocialLinks.tiktok]: SocialIcons[SocialLinks.tiktok],
    [SocialLinks.linkedin]: SocialIcons[SocialLinks.linkedin],
  };

  const currentUrl = window.location.href;

  const onShareProfile = () => {
    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        toast.success("URL copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy URL: ", err);
      });
  };

  const handleCancel = () => {
    setOpen(false);
    onCancel && onCancel();
  };

  const handleOpen = () => setOpen(!open);

  const handleDataChange = (data) => {
    setProfile(data);
  };

  useEffect(() => {
    ProfilePopProps = { handleOpen, handleCancel, handleDataChange };
    return () => (ProfilePopProps = {});
  }, []);

  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  useEffect(() => {
    if (profile) {
      setSocialLinks(
        profile?.user_profile_social_links?.map((linkItem, idx) => {
          return {
            id: idx,
            icon: social_button_asset_mapping[linkItem.platform],
            url: linkItem.link,
            onClick: () =>
              window.open(
                /^(https?:\/\/)/.test(linkItem.link)
                  ? linkItem.link
                  : "https://" + linkItem.link,
                "_blank"
              ),
          };
        })
      );

      setCtaButtons(
        profile?.external_links?.map((linkItem, idx) => {
          return {
            id: idx,
            title: linkItem.title,
            link: linkItem.link,
            iconImage: ASSETS.segmentEditor.LinkIcon,
            isEnabled: linkItem.is_cta_enabled,
          };
        })
      );
    }
  }, [profile]);

  return (
    <StyledDialog
      open={open}
      onClose={handleCancel}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <DialogTitle
        sx={{
          padding: "20px",
        }}
      >
        <Row>
          <Row>
            <UserImage image={profile?.profile_image} size={70} />
            <TextWrapper>
              <TextView variant="text">About</TextView>
              <TextView variant="title">{`${profile?.first_name} ${profile?.last_name}`}</TextView>
            </TextWrapper>
          </Row>
          <IconButton aria-label="close" onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </Row>
      </DialogTitle>
      <DialogContent>
        <TextView variant="text">{transformedText(profile?.bio)}</TextView>

        {socialLinks?.length > 0 && (
          <>
            <TextView
              variant="title"
              style={{ marginTop: 30, marginBottom: 10 }}
            >
              Social Links
            </TextView>

            <SocialLinksWrapper style={{ justifyContent: "center" }}>
              {socialLinks?.map((item) => {
                const Icon = item.icon;
                if (Icon)
                  return (
                    <SocialLinkItem onClick={item.onClick}>
                      {React.createElement(Icon, { width: 26, height: 26 })}
                    </SocialLinkItem>
                  );
              })}
            </SocialLinksWrapper>
          </>
        )}

        {ctaButtons?.map(
          (button) =>
            button.isEnabled && (
              <Button
                disabled={!button.isEnabled}
                style={
                  button.isEnabled
                    ? {
                        backgroundColor: colors.PrimaryPurple100,
                        borderRadius: 0,
                        marginTop: 20,
                      }
                    : {
                        backgroundColor: colors.lightGrey,
                        borderRadius: 0,
                        marginTop: 20,
                      }
                }
                key={button?.id}
                onClick={() =>
                  window.open(
                    /^(https?:\/\/)/.test(button.link)
                      ? button.link
                      : "https://" + button.link,
                    "_blank"
                  )
                }
              >
                <img src={button?.iconImage} width={14} height={14} />{" "}
                <span
                  style={{
                    paddingLeft: 10,
                    color: colors.PrimaryPurple,
                    fontSize: "16px",
                    fontWeight: "500",
                  }}
                >
                  {transformedText(button?.title)}
                </span>
              </Button>
            )
        )}
      </DialogContent>
    </StyledDialog>
  );
};

ProfilePopup.propTypes = {
  open: PropTypes.bool,
  onCancel: PropTypes.func,
  profileImageSrc: PropTypes.string,
  profileName: PropTypes.string,
  profileDescription: PropTypes.string,
  socialLinks: PropTypes.arrayOf(PropTypes.string),
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      iconImage: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ),
  onShareProfile: PropTypes.func,
};

export default ProfilePopup;
